import { call, put, select } from "redux-saga/effects";
import api from "../../services/api";
import store from "../../store";
import { Creators as AuthActions } from "../ducks/auth";
import { Creators as CartActions } from "../ducks/cart";
import { Creators as DeliveryContextActions } from "../ducks/deliveryContext";
import {
  Creators as StoreByUserActions,
  SELECTED_STORE_KEY
} from "../ducks/storeByUser";
import { DELIVERY_CONTEXT_KEY } from "../../util/deliveryContextStorage";
import { syncLocalCartToAPI } from "../../services/cart"

export function* signIn({ payload }) {
  const { email, senha, redirectCheckout } = payload;

  const loja = store.getState().store.data;
  const uuid = loja !== null ? loja.empresa?.uuid : '';

  // Sem o uuid da empresa a chamada virava `sessions/` e falhava com uma mensagem
  // genérica de credencial. Aborta antes de tocar na API.
  if (!uuid) {
    yield put(AuthActions.signFailure('Não foi possível identificar a loja. Recarregue a página e tente novamente.'));
    return;
  }

  let cliente = null;

  // Etapa 1 — autenticação. Só o que acontece aqui pode reprovar o login.
  try {
    // API valida credenciais e define cookies httpOnly automaticamente
    const response = yield call(api.post, `sessions/${uuid}`, {
      email,
      senha
    });

    cliente = response.data?.cliente;
  } catch (err) {
    yield put(AuthActions.signFailure(err.response?.data?.error || 'Falha ao tentar efetuar o login'));
    return;
  }

  yield call(completeSignIn, cliente, redirectCheckout)
}

function* completeSignIn(cliente, redirectCheckout) {
  // Etapa 2 — carrinho. Best-effort: uma falha aqui NÃO reprova o login (antes
  // virava `signFailure`, que disparava o `signOut` e apagava a cesta local) e
  // NÃO descarta o carrinho de convidado — `syncLocalCartToAPI` preserva a chave
  // `@ConexaoGAM:cart` quando o servidor não confirma a persistência.
  // Mantido ANTES do `signInSuccess` de propósito: com `auth.signed = true` e a
  // chave `@ConexaoGAM:cart#uuid` ainda inexistente, `getCartFromStorage` enxerga
  // carrinho vazio.
  try {
    yield syncLocalCartToAPI(cliente?.uuid)
  } catch (err) {
    console.error('[cart-sync] falha ao sincronizar a cesta no login', {
      status: err.response?.status,
      url: err.config?.url,
      message: err.message
    });
  }

  yield put(AuthActions.signInSuccess(cliente));
  localStorage.setItem('@ConexaoGAM:session', '1');

  yield put(CartActions.syncCartToLocalStorage(redirectCheckout));
}

export function* requestAccessCode({ payload }) {
  const loja = store.getState().store.data;
  const uuid = loja?.empresa?.uuid;

  try {
    const response = yield call(api.post, `sessions/${uuid}/access-code`, {
      identifier: payload.identifier
    });
    yield put(AuthActions.requestAccessCodeSuccess(response.data.message));
  } catch (err) {
    yield put(AuthActions.accessCodeFailure(
      err.response?.data?.error || "Não foi possível enviar o código. Tente novamente."
    ));
  }
}

export function* verifyAccessCode({ payload }) {
  const loja = store.getState().store.data;
  const uuid = loja?.empresa?.uuid;

  try {
    const response = yield call(api.post, `sessions/${uuid}/access-code/verify`, {
      identifier: payload.identifier,
      code: payload.code
    });
    yield call(completeSignIn, response.data?.cliente, payload.redirectCheckout);
  } catch (err) {
    yield put(AuthActions.accessCodeFailure(
      err.response?.data?.error || "Código inválido ou expirado"
    ));
  }
}

export function* signInSuccess({ payload }) {
  // Perfil do usuário mantido apenas em memória (Redux)
  // Não há escrita em localStorage — dados sensíveis não ficam expostos
}

export function* signOut({ payload }) {
  const { navigate } = payload;

  try {
    // Invalida os cookies no servidor e apaga os cookies httpOnly
    yield call(api.post, "customers/logout");
  } catch (_) {
    // Mesmo que a API falhe, limpamos o estado local
  }

  const user = yield select((state) => state.user.profile);
  localStorage.removeItem('@ConexaoGAM:session');
  yield localStorage.removeItem("@ConexaoGAM:cart");
  if (user)
    yield localStorage.removeItem(`@ConexaoGAM:cart#${user.uuid}`);

  // Endereço e farmácia do cliente anterior não podem sobreviver ao logout: o próximo
  // cliente no mesmo navegador herdaria o local de entrega, a farmácia escolhida e os dados
  // de checkout. Com `navigate` falso não há recarregamento que limpe o Redux, então o
  // estado em memória é zerado junto com as chaves.
  yield localStorage.removeItem(DELIVERY_CONTEXT_KEY);
  yield localStorage.removeItem(SELECTED_STORE_KEY);
  yield localStorage.removeItem("@ConexaoGAM:checkout");
  yield put(DeliveryContextActions.clear());
  yield put(StoreByUserActions.setResolvedStore(null));

  if (navigate)
    window.location.href = "/";
}
